import * as React from "react";
import debounce from "lodash/debounce";
import cable from "~/lib/cable";

import Spinner from "~/components/shared/Spinner";

import JobInformationStore from "~/stores/JobInformationStore";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  jobArtifactsCountQueryKey,
  jobArtifactsCountQueryOptions,
} from "~/components/build/Show/lib/queries/jobArtifactsCount";
import { Build } from "~/components/build/Show/lib/types";

type JobProps = {
  job: {
    id: string;
    basePath: string;
  };
};

type Props = JobProps & {
  build: Build;
};

type State = {
  count: number | null | undefined;
};

const getStateFromStore = (jobId: string) => JobInformationStore.get(jobId, "artifactsCount");

class OldArtifactsCount extends React.PureComponent<JobProps, State> {
  constructor(props: JobProps) {
    super(props);

    this.state = {
      count: getStateFromStore(props.job.id),
    };
  }

  reload = debounce(
    function () {
      return JobInformationStore.refetch(
        // @ts-expect-error - TS2683 - 'this' implicitly has type 'any' because it does not have a type annotation.
        this.props.job.id,
        "artifactsCount",
        // @ts-expect-error - TS2683 - 'this' implicitly has type 'any' because it does not have a type annotation.
        this.props.job.basePath + "/artifacts/count",
      );
    },
    2000,
    { leading: true, maxWait: 4000 },
  );

  subscription: any;

  componentDidMount() {
    JobInformationStore.addChangeListener(this.handleStoreChange);

    // Reload count if this job's artifacts change
    this.subscription = cable.subscriptions.create(
      { channel: "Pipelines::JobChannel", uuid: this.props.job.id },
      {
        component: this,

        received({ event }) {
          if (event === "artifacts:changed") {
            this.component.reload();
          }
        },
      },
    );

    this.reload();
  }

  componentWillUnmount() {
    JobInformationStore.removeChangeListener(this.handleStoreChange);

    this.subscription.unsubscribe();
  }

  render() {
    if (this.state.count === undefined) {
      return <Spinner className="ml-1" />;
    }

    return <span className="badge badge--outline tabular-numerals ml-1">{this.state.count}</span>;
  }

  handleStoreChange = () => {
    this.setState({ count: getStateFromStore(this.props.job.id) });
  };

  handleWebsocketEvent = ({ jobID }: { jobID: string }) => {
    if (jobID === this.props.job.id) {
      this.reload();
    }
  };
}

function NewArtifactsCount(props: Props) {
  const queryClient = useQueryClient();

  const { data: count } = useQuery(
    jobArtifactsCountQueryOptions(props.build.id, props.job.id, props.job.basePath),
  );

  React.useEffect(() => {
    const sub = cable.subscriptions.create(
      { channel: "Pipelines::JobChannel", uuid: props.job.id },
      {
        component: NewArtifactsCount,
        received({ event }) {
          if (event === "artifacts:changed") {
            queryClient.invalidateQueries({
              queryKey: jobArtifactsCountQueryKey(props.build.id, props.job.id),
              exact: true,
            });
          }
        },
      },
    );

    return () => sub.unsubscribe();
  }, []);

  if (count === undefined) {
    return <Spinner className="ml-1" />;
  }

  return <span className="badge badge--outline tabular-numerals ml-1">{count}</span>;
}

export default function ArtifactsCount(props: Props) {
  if (Features.ProgressiveBuildShow) {
    return <NewArtifactsCount {...props} />;
  }
  return <OldArtifactsCount {...props} />;
}
